/* eslint-disable react-refresh/only-export-components */
/**
 * Global YouTube playback queue.
 *
 * Replaces three independent, route-scoped `useState` owners that each mounted
 * their own <YouTubeAutoplayer>:
 *   - src/pages/Playlists.tsx                            (`nowPlaying`)
 *   - src/components/playlist/PlaylistTracklistView.tsx   (`autoplayStartIndex`)
 *   - src/components/generate/TrackList.tsx               (`autoplayStartIndex`)
 *
 * Each sat inside a <Routes> element, so a route change unmounted the subtree
 * and the autoplayer cleanup called `player.destroy()`. Playback could not
 * survive navigation by construction. This context lives above <Routes> and
 * owns the only queue, so navigating is a no-op for playback.
 *
 * WHY THE QUEUE IS PRE-FILTERED TO PLAYABLE TRACKS: the old component took the
 * FULL track list plus an index into it, and mapped that index onto the
 * playable subset internally. That mapping produced the same class of bug twice
 * (`mapStartIndexToPlayableIdx`, deleted with that component). Callers now hand
 * over only tracks carrying a youtube_video_id, so an index means one thing
 * everywhere and there is no mapping layer left to get wrong.
 *
 * RELOAD BEHAVIOUR IS CUE-AND-WAIT, DELIBERATELY. Browser autoplay policy
 * requires a user gesture for unmuted playback and the YT IFrame API inherits
 * it, so audio cannot resume on its own after a reload. We restore the queue,
 * the track and the position, cue the player there, and wait for one tap
 * (`awaitingResume`). The alternative, muted auto-resume, continues silently,
 * which reads as broken to anyone who reloads mid-song.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useReducer,
  useState,
  type ReactNode,
} from "react";
import { safeGetItem, safeRemoveItem, safeSetItem } from "@/lib/utils";

export interface PlayerTrack {
  id?: string | null;
  uri?: string | null;
  name: string;
  artist: string;
  /** Always non-empty in a queue: callers filter before handing tracks over. */
  youtube_video_id: string;
  /** Album art, used for the now-playing thumb and the art-reactive tint. */
  image?: string | null;
}

export interface PlayerQueue {
  /** Playlist db id where available, else a synthetic id for the generate page.
   *  Drives the "this card is playing" highlight on listing surfaces and lets a
   *  repeat play request be recognised as the same source. */
  sourceId: string;
  /** Human label for the player bar. */
  sourceLabel: string;
  tracks: PlayerTrack[];
  /** Hand-off targets. Spotify's embed is cross-origin so it can offer no
   *  transport at all; the player links out rather than rendering dead keys.
   *  Apple Music is absent from every row today, so its option only appears
   *  once a playlist actually carries one. */
  spotifyPlaylistId?: string | null;
  appleMusicPlaylistId?: string | null;
}

const STORAGE_KEY = "spotivibly_yt_player_v1";
/** Anything older is discarded on boot: resuming a week-old track is noise. */
const MAX_RESTORE_AGE_MS = 12 * 60 * 60 * 1000;
/** Queues are capped before persisting. A 100-track queue is ~15KB of JSON and
 *  localStorage is a 5MB budget shared with the rest of the app. */
const MAX_PERSISTED_TRACKS = 200;
/** How many tracks before the current one a capped snapshot keeps, so Previous
 *  still has somewhere to go after a reload. */
const PERSISTED_TRACKS_BEFORE = 50;
/** Position write cadence while playing. */
const PERSIST_INTERVAL_MS = 5000;

interface PersistedPlayer {
  queue: PlayerQueue;
  index: number;
  positionSeconds: number;
  savedAt: number;
}

/** Why a queue stopped on its own. Null while it is still going. */
export type QueueEnd = "completed" | "unavailable";

/** Consecutive unplayable tracks before the queue gives up. Error 150 fired
 *  twice in a five-track queue on a real device, so a run of two or three is
 *  ordinary; five in a row means the problem is not the tracks. */
export const MAX_CONSECUTIVE_SKIPS = 5;

export interface PlayerQueueContextType {
  queue: PlayerQueue | null;
  index: number;
  /** What the listener ASKED for: audio running or not. The player reports
   *  what YouTube is actually doing through `reportObserved`, and the two are
   *  kept apart on purpose: when one boolean carried both, YouTube's
   *  transitional PAUSED during a track switch came back as a pause request
   *  and paused the track that had just been asked for. */
  isPlaying: boolean;
  /** True only between a reload-restore and the first tap. The player is cued
   *  at `resumeAt` but silent, because policy forbids unmuted autoplay. */
  awaitingResume: boolean;
  /** Seconds to seek to when playback starts. 0 for a fresh queue. */
  resumeAt: number;
  /** The current track, or null when nothing is queued. */
  currentTrack: PlayerTrack | null;
  /** Bumped by every track request: a pick, next, prev, random, an automatic
   *  advance, a skip, a replay. The player loads on THIS, not on the video id,
   *  because two queue entries can share one video and "same id" is not "same
   *  request". */
  requestId: number;
  /** The browser refused to start audio without a gesture. The UI offers a
   *  tap; nothing retries on its own. */
  blocked: boolean;
  /** Set when the queue stopped by itself: it ran out, or it ran out of
   *  tracks YouTube would play. */
  ended: QueueEnd | null;
  /** Name of the last track skipped as unplayable, for the status line. */
  lastSkipped: string | null;

  /** A play button on the site. Also asks the console to open maximised. */
  playQueue: (queue: PlayerQueue, startIndex?: number) => void;
  /** `present` is for a play button on the site: the console opens
   *  maximised, as it does for `playQueue`. The console's own picks (queue
   *  rows, Try again) leave it at whatever size it is. */
  playIndex: (index: number, opts?: { present?: boolean }) => void;
  next: () => void;
  prev: () => void;
  /** One random OTHER track, then sequential again. Not a shuffle mode. */
  playRandom: (rng?: () => number) => void;
  /** Start the queue again from its first track. */
  replay: () => void;
  /** Automatic advance when a track ends. Not a gesture: it keeps the current
   *  playback intent and stops, without wrapping, after the last track.
   *  `fromIndex` makes a duplicate ENDED for the same track a no-op. */
  advance: (fromIndex?: number) => void;
  /** Advance past a track the player could not load, WITHOUT treating it as a
   *  user action. Keeps `awaitingResume` intact so a reload that lands on an
   *  unplayable track stays silent instead of becoming autoplay. */
  skipUnplayable: (fromIndex?: number) => void;
  /** A listener's request to play or pause. */
  setPlaying: (playing: boolean) => void;
  /** What the player observed. Never issues a command back to the player. */
  reportObserved: (playing: boolean) => void;
  /** The browser blocked an attempt to start audio. */
  markBlocked: () => void;
  togglePlay: () => void;
  close: () => void;
  /** Called by the player on a timer so a reload can restore the position. */
  reportPosition: (seconds: number) => void;
  /** Clears `awaitingResume` once playback has actually started. */
  clearResume: () => void;
  /** Bumped by `viewPlayer`. The console expands and takes focus on each
   *  change; 0 means nobody has asked. */
  viewRequest: number;
  /** Ask the console to open full size and move focus into it. For a surface
   *  the console sits behind, such as the detail dialog, which closes itself
   *  first so two modal scopes never compete for focus. */
  viewPlayer: () => void;
  /** Bumped by every play request from the site, never by the console's own
   *  keys, media keys or an automatic advance. The console opens maximised on
   *  each change, unless another modal is open over the page. */
  presentRequest: number;
  /** Where the console sends the listener. "spotify" is a hand-off: the
   *  console pauses and offers Open in Spotify, because Spotify's player is
   *  cross-origin and nothing here can drive it. Any explicit YouTube request
   *  takes it back to "youtube". Lives here rather than in the console so the
   *  mini bar and the full console cannot disagree about it. */
  output: PlayerOutput;
  /** Choose the output. Spotify pauses YouTube; YouTube only changes the
   *  output and never resumes on its own. */
  setOutput: (output: PlayerOutput) => void;
  /** Bumped by every explicit request for YouTube audio: a pick, Play, Resume.
   *  Never by an automatic advance or by what the player observes. Other
   *  app-managed players watch it to give way. */
  playRequest: number;
}

export type PlayerOutput = "youtube" | "spotify";

const PlayerQueueContext = createContext<PlayerQueueContextType | undefined>(
  undefined,
);

/** For a component that can render outside the provider, such as a Vibes
 *  card in a test: undefined instead of a throw. */
export const useOptionalPlayerQueue = () => useContext(PlayerQueueContext);

export const usePlayerQueue = () => {
  const ctx = useContext(PlayerQueueContext);
  if (!ctx) {
    throw new Error("usePlayerQueue must be used within PlayerQueueProvider");
  }
  return ctx;
};

/**
 * localStorage is user-mutable and survives deploys, so a hand-edited or stale
 * entry would otherwise flow into an iframe src as a string. Same discipline as
 * `isValidPlaylistQueueItem` in SpotifyPlayerContext.
 *
 * Exported for the unit tests.
 */
export function isValidPersistedPlayer(
  parsed: unknown,
): parsed is PersistedPlayer {
  if (!parsed || typeof parsed !== "object") return false;
  const p = parsed as Record<string, unknown>;
  // An integer, not just a finite number: tracks[0.5] is undefined too.
  if (typeof p.index !== "number" || !Number.isInteger(p.index)) return false;
  if (
    typeof p.positionSeconds !== "number" ||
    !Number.isFinite(p.positionSeconds) ||
    p.positionSeconds < 0
  ) {
    return false;
  }
  if (typeof p.savedAt !== "number" || !Number.isFinite(p.savedAt)) return false;

  const q = p.queue as Record<string, unknown> | undefined;
  if (!q || typeof q !== "object") return false;
  if (typeof q.sourceId !== "string" || typeof q.sourceLabel !== "string") {
    return false;
  }
  // Optional, but must be the right shape when present: these end up in an
  // href, and localStorage is user-mutable.
  for (const k of ["spotifyPlaylistId", "appleMusicPlaylistId"] as const) {
    const v = q[k];
    if (v !== undefined && v !== null && typeof v !== "string") return false;
  }
  if (!Array.isArray(q.tracks) || q.tracks.length === 0) return false;

  const tracksValid = q.tracks.every((t) => {
    if (!t || typeof t !== "object") return false;
    const tt = t as Record<string, unknown>;
    // Optional fields are consumed too: image becomes an <img> src and feeds
    // the art tint, so a non-string there is as wrong as a missing name.
    const optionalOk = (["id", "uri", "image"] as const).every(
      (k) => tt[k] === undefined || tt[k] === null || typeof tt[k] === "string",
    );
    return (
      optionalOk &&
      typeof tt.name === "string" &&
      typeof tt.artist === "string" &&
      typeof tt.youtube_video_id === "string" &&
      tt.youtube_video_id.length > 0
    );
  });
  if (!tracksValid) return false;

  // An index outside the queue would cue `undefined` into the player.
  return p.index >= 0 && p.index < q.tracks.length;
}

/**
 * Is the live queue the SAME LIST this surface is showing, such that reusing
 * the index is safe?
 *
 * The source id alone cannot answer it. /generate reuses one synthetic draft id
 * for every generation, so generating twice produces two different track lists
 * under one id; reusing the index there seeks a stale queue and plays the wrong
 * song.
 *
 * It compares the WHOLE ordered list. It used to compare one video at the
 * clicked index, which a list with the same head and a different tail passes:
 * an edited playlist kept playing its old tail. Ordered video ids are the
 * whole identity a queue index depends on, so equal lists mean equal indices.
 *
 * Exported and tested because it is shared by TrackList and
 * PlaylistTracklistView, and because the failure it prevents is silent: the
 * wrong track plays and nothing errors.
 */
export function isSameQueue(
  live: PlayerQueue | null,
  sourceId: string,
  tracks: readonly { youtube_video_id: string }[],
): boolean {
  if (!live || live.sourceId !== sourceId) return false;
  if (live.tracks.length !== tracks.length) return false;
  return live.tracks.every((t, i) => t.youtube_video_id === tracks[i].youtube_video_id);
}

/** A row as a list surface holds it: the video id may be missing. */
export interface QueueRow {
  id?: string | null;
  uri?: string | null;
  name: string;
  artist: string;
  image?: string | null;
  youtube_video_id?: string | null;
}

/**
 * The playable queue for a list, and where each row sits in it.
 *
 * Only rows with a video reach the queue, so row i and queue position differ.
 * The mapping is by POSITION, never by video id: the old lookup was
 * `findIndex(videoId)`, which sends the second of two rows sharing a video to
 * the first. Shared by TrackList and PlaylistTracklistView.
 */
export function buildPlayableQueue(rows: readonly QueueRow[]): {
  tracks: PlayerTrack[];
  /** Queue position for each row, or null for a row with no video. */
  queueIndexByRow: (number | null)[];
} {
  const tracks: PlayerTrack[] = [];
  const queueIndexByRow = rows.map((t) => {
    if (!t.youtube_video_id) return null;
    tracks.push({
      id: t.id ?? null,
      uri: t.uri ?? null,
      name: t.name,
      artist: t.artist,
      image: t.image ?? null,
      youtube_video_id: t.youtube_video_id,
    });
    return tracks.length - 1;
  });
  return { tracks, queueIndexByRow };
}

function readPersisted(now: number): PersistedPlayer | null {
  try {
    const raw = safeGetItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isValidPersistedPlayer(parsed)) return null;
    if (now - parsed.savedAt > MAX_RESTORE_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

interface QueueState {
  queue: PlayerQueue | null;
  index: number;
  isPlaying: boolean;
  awaitingResume: boolean;
  resumeAt: number;
  requestId: number;
  blocked: boolean;
  ended: QueueEnd | null;
  skips: number;
  lastSkipped: string | null;
  output: PlayerOutput;
  playRequest: number;
}

type QueueAction =
  | { type: "playQueue"; queue: PlayerQueue; startIndex: number }
  | { type: "select"; index: number }
  | { type: "advance"; fromIndex?: number }
  | { type: "skip"; fromIndex?: number }
  | { type: "intent"; playing: boolean }
  | { type: "observed"; playing: boolean }
  | { type: "blocked" }
  | { type: "clearResume" }
  | { type: "output"; output: PlayerOutput }
  | { type: "close" };

function initialState(restored: PersistedPlayer | null): QueueState {
  return {
    queue: restored?.queue ?? null,
    index: restored?.index ?? 0,
    isPlaying: false,
    awaitingResume: !!restored,
    resumeAt: restored?.positionSeconds ?? 0,
    requestId: 0,
    blocked: false,
    ended: null,
    skips: 0,
    lastSkipped: null,
    output: "youtube",
    playRequest: 0,
  };
}

/** A fresh, explicit request for the track at `index`: a gesture, so it
 *  clears every waiting state and asks for audio. */
function selectTrack(s: QueueState, index: number): QueueState {
  return {
    ...s,
    index,
    isPlaying: true,
    awaitingResume: false,
    resumeAt: 0,
    requestId: s.requestId + 1,
    blocked: false,
    ended: null,
    skips: 0,
    // Picking a track is asking for YouTube, so a hand-off ends here.
    output: "youtube",
    playRequest: s.playRequest + 1,
  };
}

/** Exported for the unit tests. */
export function queueReducer(s: QueueState, a: QueueAction): QueueState {
  const total = s.queue?.tracks.length ?? 0;
  switch (a.type) {
    case "playQueue": {
      // Nothing to play: keep whatever is live rather than mounting a player
      // with no current track.
      if (a.queue.tracks.length === 0) return s;
      const lastIndex = Math.max(a.queue.tracks.length - 1, 0);
      const start = Number.isInteger(a.startIndex) ? a.startIndex : 0;
      const safeIndex = Math.min(Math.max(start, 0), lastIndex);
      return { ...selectTrack(s, safeIndex), queue: a.queue, lastSkipped: null };
    }
    case "select":
      if (!s.queue || !Number.isInteger(a.index) || a.index < 0 || a.index >= total) {
        return s;
      }
      return selectTrack(s, a.index);
    case "advance": {
      if (!s.queue) return s;
      // A second ENDED for the track we already left must not skip one more.
      if (a.fromIndex !== undefined && a.fromIndex !== s.index) return s;
      if (s.index + 1 >= total) {
        return { ...s, isPlaying: false, ended: "completed" };
      }
      return { ...s, index: s.index + 1, resumeAt: 0, requestId: s.requestId + 1 };
    }
    case "skip": {
      if (!s.queue) return s;
      if (a.fromIndex !== undefined && a.fromIndex !== s.index) return s;
      const skips = s.skips + 1;
      const lastSkipped = s.queue.tracks[s.index]?.name ?? null;
      if (s.index + 1 >= total || skips >= MAX_CONSECUTIVE_SKIPS) {
        // Nothing left that will play. Stop and say so, rather than leaving a
        // Pause key over a silent player.
        return { ...s, skips, lastSkipped, isPlaying: false, ended: "unavailable" };
      }
      // Deliberately does NOT touch awaitingResume or isPlaying: a skip
      // inherits whatever playback state it interrupted.
      return {
        ...s,
        skips,
        lastSkipped,
        index: s.index + 1,
        resumeAt: 0,
        requestId: s.requestId + 1,
      };
    }
    case "intent":
      if (!a.playing) return { ...s, isPlaying: false };
      return {
        ...s,
        isPlaying: true,
        awaitingResume: false,
        blocked: false,
        output: "youtube",
        playRequest: s.playRequest + 1,
      };
    case "observed":
      if (!a.playing) return { ...s, isPlaying: false };
      // Audio is running, so every reason to wait is over. YouTube is what is
      // audible, so the console stops offering a hand-off over it.
      return {
        ...s,
        isPlaying: true,
        output: "youtube",
        awaitingResume: false,
        blocked: false,
        ended: null,
        skips: 0,
      };
    case "blocked":
      return { ...s, isPlaying: false, blocked: true };
    case "clearResume":
      return { ...s, awaitingResume: false };
    case "output":
      // Handing off pauses: audio running behind an Open in Spotify button
      // would be two players at once. Coming back does NOT resume. The
      // listener left YouTube on purpose, so its return waits for Play.
      if (a.output === "spotify") return { ...s, output: "spotify", isPlaying: false };
      return { ...s, output: "youtube" };
    case "close":
      return { ...initialState(null), requestId: s.requestId + 1 };
  }
}

export const PlayerQueueProvider = ({ children }: { children: ReactNode }) => {
  // Read once, synchronously, so the first paint already carries the restored
  // bar rather than flashing an empty player in.
  const [state, dispatch] = useReducer(queueReducer, null, () =>
    initialState(typeof window === "undefined" ? null : readPersisted(Date.now())),
  );
  const { queue, index, isPlaying, awaitingResume, resumeAt } = state;

  // Position changes about once a second while playing. Keeping it in a ref
  // rather than state means the whole tree does not re-render once a second for
  // a value only the persistence effect reads.
  const positionRef = useRef(state.resumeAt);
  // A new request starts its track from the top; the ref follows.
  const lastRequestRef = useRef(state.requestId);
  if (lastRequestRef.current !== state.requestId) {
    lastRequestRef.current = state.requestId;
    positionRef.current = state.resumeAt;
  }

  const reportPosition = useCallback((seconds: number) => {
    if (Number.isFinite(seconds) && seconds >= 0) positionRef.current = seconds;
  }, []);

  const writeSnapshot = useCallback(() => {
    if (!queue) return;
    try {
      // A capped snapshot is a WINDOW around the current track. It used to be
      // the first 200, so a listener on track 240 saved an index outside the
      // saved queue, validation rejected it, and the reload restored nothing.
      const start = Math.min(
        Math.max(0, index - PERSISTED_TRACKS_BEFORE),
        Math.max(0, queue.tracks.length - MAX_PERSISTED_TRACKS),
      );
      const payload: PersistedPlayer = {
        queue: { ...queue, tracks: queue.tracks.slice(start, start + MAX_PERSISTED_TRACKS) },
        index: index - start,
        positionSeconds: positionRef.current,
        savedAt: Date.now(),
      };
      safeSetItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Quota or privacy mode. Playback is unaffected; only resume is lost.
    }
  }, [queue, index]);

  // Persist on every queue/index change, and on a slow timer while playing so a
  // reload lands near where the user actually was.
  useEffect(() => {
    if (!queue) {
      try {
        safeRemoveItem(STORAGE_KEY);
      } catch {
        // localStorage unavailable
      }
      return;
    }
    writeSnapshot();
    if (!isPlaying) return;
    const id = window.setInterval(writeSnapshot, PERSIST_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, [queue, isPlaying, writeSnapshot]);

  // A tab closing mid-track would otherwise lose up to PERSIST_INTERVAL_MS of
  // position. pagehide fires on iOS Safari where beforeunload does not, which
  // is the same reason the service worker uses it for keepalive fetches.
  useEffect(() => {
    window.addEventListener("pagehide", writeSnapshot);
    return () => window.removeEventListener("pagehide", writeSnapshot);
  }, [writeSnapshot]);

  // Read by the actions below so they stay referentially stable: a context
  // whose callbacks change identity every track re-renders every consumer.
  const stateRef = useRef(state);
  stateRef.current = state;

  const [presentRequest, setPresentRequest] = useState(0);
  const playQueue = useCallback((nextQueue: PlayerQueue, startIndex = 0) => {
    dispatch({ type: "playQueue", queue: nextQueue, startIndex });
    if (nextQueue.tracks.length > 0) setPresentRequest((n) => n + 1);
  }, []);
  const playIndex = useCallback((i: number, opts?: { present?: boolean }) => {
    dispatch({ type: "select", index: i });
    if (opts?.present) setPresentRequest((n) => n + 1);
  }, []);
  const next = useCallback(() => {
    const s = stateRef.current;
    if (s.queue && s.index + 1 < s.queue.tracks.length) {
      dispatch({ type: "select", index: s.index + 1 });
    }
  }, []);
  const prev = useCallback(() => {
    const s = stateRef.current;
    if (s.queue && s.index > 0) dispatch({ type: "select", index: s.index - 1 });
  }, []);
  const playRandom = useCallback((rng: () => number = Math.random) => {
    const s = stateRef.current;
    const total = s.queue?.tracks.length ?? 0;
    if (total < 2) return;
    // One draw over the OTHER tracks, then step past the current one. The old
    // rejection loop redrew until it missed the current index, which spins for
    // ever on an RNG that keeps landing there.
    const raw = rng();
    const r = Number.isFinite(raw) ? Math.min(Math.max(raw, 0), 0.999999) : 0;
    let n = Math.floor(r * (total - 1));
    if (n >= s.index) n += 1;
    dispatch({ type: "select", index: n });
  }, []);
  const replay = useCallback(() => dispatch({ type: "select", index: 0 }), []);
  const advance = useCallback(
    (fromIndex?: number) => dispatch({ type: "advance", fromIndex }),
    [],
  );
  const skipUnplayable = useCallback(
    (fromIndex?: number) => dispatch({ type: "skip", fromIndex }),
    [],
  );
  const setPlaying = useCallback(
    (playing: boolean) => dispatch({ type: "intent", playing }),
    [],
  );
  const reportObserved = useCallback(
    (playing: boolean) => dispatch({ type: "observed", playing }),
    [],
  );
  const markBlocked = useCallback(() => dispatch({ type: "blocked" }), []);

  const togglePlay = useCallback(() => {
    const s = stateRef.current;
    // Play on a finished queue means "again", and on a queue that ran out of
    // playable tracks it means "try this one again".
    if (!s.isPlaying && s.ended === "completed") {
      dispatch({ type: "select", index: 0 });
      return;
    }
    if (!s.isPlaying && s.ended === "unavailable") {
      dispatch({ type: "select", index: s.index });
      return;
    }
    dispatch({ type: "intent", playing: !s.isPlaying });
  }, []);

  const clearResume = useCallback(() => dispatch({ type: "clearResume" }), []);

  const close = useCallback(() => {
    dispatch({ type: "close" });
    positionRef.current = 0;
    try {
      safeRemoveItem(STORAGE_KEY);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const setOutput = useCallback(
    (output: PlayerOutput) => dispatch({ type: "output", output }),
    [],
  );

  const [viewRequest, setViewRequest] = useState(0);
  const viewPlayer = useCallback(() => setViewRequest((n) => n + 1), []);

  const currentTrack = queue?.tracks[index] ?? null;

  const value = useMemo<PlayerQueueContextType>(
    () => ({
      queue,
      index,
      isPlaying,
      awaitingResume,
      resumeAt,
      currentTrack,
      requestId: state.requestId,
      blocked: state.blocked,
      ended: state.ended,
      lastSkipped: state.lastSkipped,
      playQueue,
      playIndex,
      next,
      prev,
      playRandom,
      replay,
      advance,
      skipUnplayable,
      setPlaying,
      reportObserved,
      markBlocked,
      togglePlay,
      close,
      reportPosition,
      clearResume,
      viewRequest,
      viewPlayer,
      presentRequest,
      output: state.output,
      setOutput,
      playRequest: state.playRequest,
    }),
    [
      queue,
      index,
      isPlaying,
      awaitingResume,
      resumeAt,
      currentTrack,
      state.requestId,
      state.blocked,
      state.ended,
      state.lastSkipped,
      playQueue,
      playIndex,
      next,
      prev,
      playRandom,
      replay,
      advance,
      skipUnplayable,
      setPlaying,
      reportObserved,
      markBlocked,
      togglePlay,
      close,
      reportPosition,
      clearResume,
      viewRequest,
      viewPlayer,
      presentRequest,
      state.output,
      setOutput,
      state.playRequest,
    ],
  );

  return (
    <PlayerQueueContext.Provider value={value}>
      {children}
    </PlayerQueueContext.Provider>
  );
};
